import { useEffect, useRef, useState, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { find } from "lodash";

import { FormValidationMethods } from "@/components/Form/types";
import { getErrorMessage } from "../../Input/adapter/utils";
import { checkValidHexColor } from "@/utils/formHelper";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { useDebounce } from "@/hooks/utils/useDebounce";

interface UseColorPickerAdapterOptions {
  name: string;
  defaultValue?: string;
  value?: string;
  errorMessage?: any;
  disabled?: boolean;
  onChange?: (color: string) => void;
  rules?: any;
}

export const useColorPickerAdapter = ({
  name,
  defaultValue,
  value: externalValue,
  errorMessage,
  disabled,
  onChange,
  rules,
}: UseColorPickerAdapterOptions) => {
  const methods: FormValidationMethods = useFormContext();
  const registeredRef = useRef(false);
  const qrEditorContext = useContext<QREditorContextProps<any>>(QREditorContext);
  const { qrData, setQrData } = qrEditorContext || {};

  const qrStyleValue = qrData?.qrStyle?.[name];
  const lastContextUpdate = useRef<string | null>(qrStyleValue || null);

  const [colorValue, setColorValue] = useState<string>(
    qrStyleValue || externalValue || defaultValue || ""
  );

  const debouncedColorValue = useDebounce(colorValue, 50);

  useEffect(() => {
    if (!methods || !name) return;

    const schemaRules = methods.schema && find(methods.schema, { name });

    const finalRules: any = {
      ...(schemaRules?.rules || {}),
      ...(rules || {}),
    };

    methods.register(name, finalRules);
    registeredRef.current = true;
  }, [name, methods, rules]);

  useEffect(() => {
    const initialValue = qrStyleValue || externalValue || defaultValue;
    if (initialValue !== undefined && registeredRef.current) {
      methods?.setValue(name, initialValue);
      setColorValue(initialValue);
    }
  }, []);

  useEffect(() => {
    if (!qrEditorContext || !setQrData) return;

    const isValid = checkValidHexColor(debouncedColorValue);
    if (!isValid) return;

    if (qrData?.qrStyle?.[name] === debouncedColorValue) return;

    lastContextUpdate.current = debouncedColorValue;

    setQrData({
      ...qrData,
      qrStyle: {
        ...qrData?.qrStyle,
        [name]: debouncedColorValue,
      },
    });
  }, [debouncedColorValue]);

  useEffect(() => {
    if (!qrStyleValue) return;

    const currentValueIsInvalid = !checkValidHexColor(colorValue);
    if (currentValueIsInvalid) return;

    const isExternalChange =
      qrStyleValue !== colorValue && qrStyleValue !== lastContextUpdate.current;

    if (isExternalChange) {
      lastContextUpdate.current = qrStyleValue;
      setColorValue(qrStyleValue);
      methods?.setValue(name, qrStyleValue);
      methods?.clearErrors(name);
    }
  }, [qrStyleValue]);

  const handleChange = (color: string) => {
    setColorValue(color);
    methods?.setValue(name, color);

    methods?.trigger(name);

    if (onChange) {
      onChange(color);
    }
  };

  const currentValue = colorValue;

  const currentErrorMessage = errorMessage || methods?.formState?.errors?.[name];
  const finalErrorMessage: string | undefined = currentErrorMessage
    ? getErrorMessage(currentErrorMessage)
    : undefined;

  return {
    currentValue,
    finalErrorMessage,
    isDisabled: disabled,
    handleChange,
  };
};
